import { useTranslation } from 'react-i18next';
import { cx } from './cx';

/** Pagination (GOV.UK pattern; not part of ID-SK 2.11). Pages are 0-based internally. */
export function Pagination({ page, totalPages, totalElements, onPage }: { page: number; totalPages: number; totalElements: number; onPage: (page: number) => void }) {
  const { t } = useTranslation();
  if (totalPages <= 1) {
    return (
      <p className="govuk-body app-pagination__summary" role="status">
        {t('pagination.total', { count: totalElements })}
      </p>
    );
  }
  const pages = visiblePages(page, totalPages);
  return (
    <nav className="app-pagination" aria-label={t('pagination.label')}>
      {page > 0 && (
        <button type="button" className="govuk-link app-pagination__link app-link-button" onClick={() => onPage(page - 1)}>
          {t('pagination.previous')} <span className="govuk-visually-hidden">{t('pagination.page')}</span>
        </button>
      )}
      <ul className="app-pagination__list">
        {pages.map((p, i) =>
          p === null ? (
            <li className="app-pagination__item" key={`gap-${i}`}>
              <span aria-hidden="true">…</span>
            </li>
          ) : (
            <li className={cx('app-pagination__item', p === page && 'app-pagination__item--current')} key={p}>
              {p === page ? (
                <span aria-current="page">
                  <span className="govuk-visually-hidden">{t('pagination.page')}</span> {p + 1}
                </span>
              ) : (
                <button type="button" className="govuk-link app-link-button" onClick={() => onPage(p)} aria-label={t('pagination.goTo', { page: p + 1 })}>
                  {p + 1}
                </button>
              )}
            </li>
          ),
        )}
      </ul>
      {page < totalPages - 1 && (
        <button type="button" className="govuk-link app-pagination__link app-link-button" onClick={() => onPage(page + 1)}>
          {t('pagination.next')} <span className="govuk-visually-hidden">{t('pagination.page')}</span>
        </button>
      )}
      <p className="govuk-body app-pagination__summary" role="status">
        {t('pagination.summary', { page: page + 1, totalPages, count: totalElements })}
      </p>
    </nav>
  );
}

function visiblePages(page: number, total: number): (number | null)[] {
  const result: (number | null)[] = [];
  for (let p = 0; p < total; p++) {
    if (p === 0 || p === total - 1 || Math.abs(p - page) <= 1) result.push(p);
    else if (result[result.length - 1] !== null) result.push(null);
  }
  return result;
}
